import { TemplateResult } from 'lit-html';
import { GzGenericWidgetContent } from '../../../genericDashboard/gzGenericWidget';
import { styles } from '../view/styles';
import { html, nothing } from 'lit';
import {
  ButtonProps,
  ICON_RISK_MANAGEMENT,
  RiskIncidentOverviewSectionData,
  RiskIncidentOverviewSectionRow,
  RisksOverviewSectionEvents,
  StatusMessage,
} from '../types';
import { WEB_COMPONENT_RISK_INCIDENT_SECTION } from '../../../webcomponents.constants';
import { customElement, property } from 'lit/decorators.js';
import { ButtonColor } from '../../../ui/gzIconButton/types';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { ENTITIES_CLASS } from '../../../edr/incidentsGrid/types';
import { WidgetStateFrontEnd } from '../../../genericDashboard/gzGenericWidget/types';

@customElement(WEB_COMPONENT_RISK_INCIDENT_SECTION)
export class RiskIncidentOverviewSectionWebComponent extends GzGenericWidgetContent {
  @property({ type: Object, attribute: true, reflect: true })
  payload: RiskIncidentOverviewSectionData;

  @property({ type: Object, attribute: true, reflect: true })
  numberOfItemsToDisplay: number;

  @property({ type: Object, attribute: true, reflect: true })
  displayExceedingItems: boolean;

  @property({ type: Object, attribute: true, reflect: true })
  totalEntities: number;

  @property({ type: Object, attribute: true, reflect: true })
  statusMessage: StatusMessage;

  @property({ type: Object, attribute: true, reflect: true })
  buttonProps: ButtonProps;

  static get styles() {
    return styles;
  }

  public renderContent(): TemplateResult {
    const section = this.payload as RiskIncidentOverviewSectionData;
    const items = [] as TemplateResult[];

    let statusMessage;
    if (this.statusMessage) {
      statusMessage = this.renderStatusMessage(this.statusMessage);
    }

    if (this.state === WidgetStateFrontEnd.IS_ERROR) {
      return html`${nothing}`;
    }

    if (this.state === WidgetStateFrontEnd.IS_EMPTY) {
      return this.statusMessage ? this.renderEmptySection(statusMessage) : html`${nothing}`;
    }

    const numberOfItemsToDisplay = this.displayExceedingItems
      ? section.data.length
      : Math.min(this.numberOfItemsToDisplay, section.data.length);
    for (let indexOfItem = 0; indexOfItem < numberOfItemsToDisplay; indexOfItem++) {
      items.push(this.renderSectionItem(section, section.data[indexOfItem] ?? undefined));
    }

    return html`
      <div class="section">
        ${this.renderSectionTitle(section)}
        <div class="items">
          ${items}
          <div class="status-message">
            <div class="status-message-info">${statusMessage}</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderEmptySection(statusMessage: TemplateResult): TemplateResult {
    return html`
      <div class="section">
        <div class="items">
          <div class="status-message">
            <div class="status-message-info">${statusMessage}</div>
          </div>
        </div>
      </div>
    `;
  }

  private handleOnClickButton(detailForEvent: number): void {
    this.dispatchEvent(
      new CustomEvent(RisksOverviewSectionEvents.CLICK_ON_CUSTOM_BUTTON, {
        detail: {
          eventType: detailForEvent,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderStatusMessage(statusMessage: StatusMessage): TemplateResult {
    statusMessage.iconSize = statusMessage.iconSize || '20';
    statusMessage.iconColor = statusMessage.iconColor || ButtonColor.SECONDARY;
    let buttonHTML;
    if (this.buttonProps) {
      buttonHTML = html`
        <gz-button
          id="view-all-risks-button"
          color="primary"
          type="quiet"
          buttonheight="small"
          @click="${() => this.handleOnClickButton(this.buttonProps.detailForEvent)}"
          buttonfontsize="small"
        >
          ${this.buttonProps.buttonText}
        </gz-button>
      `;
    }
    return html`
      <gz-icon size="${statusMessage.iconSize}" name="${statusMessage.icon}" color="${statusMessage.iconColor}">
      </gz-icon>
      <div class="status-message-text-button">
        <div class="status-text-div">
            <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.BOLD}" class="first-text"
              >${statusMessage.firstText}
            </gz-text>
            <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.MEDIUM}" class="second-text"
              >${statusMessage.secondText}
            </gz-text>
        </div>
        ${buttonHTML}
      </div>
    `;
  }

  protected renderSectionTitle(section: RiskIncidentOverviewSectionData): TemplateResult | symbol {
    if (!section.title) {
      return nothing;
    }
    return html`
      <gz-tooltip isEllipsis tooltipText="${section.title}">
        <div class="section-title fs-11">${section.title}</div>
      </gz-tooltip>
    `;
  }

  public renderSectionItem(
    section: RiskIncidentOverviewSectionData,
    row: RiskIncidentOverviewSectionRow = {
      entities: [],
      type: null,
      name: null,
      status: null,
      severityScore: null,
    }
  ): TemplateResult {
    let nameOfRow = row.name;
    let iconString;
    let iconTooltipString = '';
    row.type = row.entities[0];
    if (row.entities.length === 1) {
      iconString = ENTITIES_CLASS.get(row.type);
      iconTooltipString = Translator.translateXdrEntityType(row.entities[0]);
    }
    if (row.entities.length > 1) {
      const entitiesNames: string[] = [];
      for (const entity of row.entities) {
        entitiesNames.push(Translator.translateXdrEntityType(entity));
      }
      iconTooltipString = entitiesNames.join(', ');
      iconString = ICON_RISK_MANAGEMENT;
      nameOfRow += ' (' + row.entities.length + ')';
    }
    return html`
      <gz-risk-overview-section-item
        .name=${nameOfRow}
        .icon=${iconString}
        .iconTooltipString="${iconTooltipString}"
        .severityScore=${row.severityScore}
        .typeOfBullet=${row.status}
        .type=${row.type}
      />
    `;
  }
}
